import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';

import { InputOtpModule } from 'primeng/inputotp';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';

import { MemberService } from '../../services/member';
import { ToastService } from '../../services/toast';

/**
 * Step 3 of the member-onboarding wizard — OTP Verification / KYC.
 *
 * After the member is saved the parent hands over the memberId plus the OTP
 * metadata from the Save response (channels, expiry, the guiding message and
 * whether an OTP actually went out). This component collects a 6-digit code,
 * verifies it, supports resend with a 30s cooldown and shows a live mm:ss
 * expiry countdown. On a successful verify it emits {@link verified} so the
 * parent can redirect.
 *
 * All timer work is browser-only (this app uses SSR), so every setInterval is
 * wrapped in an isPlatformBrowser guard and torn down in ngOnDestroy. The
 * countdown is derived from a stored end timestamp (Date.now()-based) so it
 * survives re-renders and tab visibility changes.
 */
@Component({
  selector: 'app-otp-step',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, InputOtpModule, ButtonModule, MessageModule],
  templateUrl: './otp-step.html',
  styleUrl: './otp-step.css',
})
export class OtpStep implements OnChanges, OnDestroy {
  private memberService = inject(MemberService);
  private toast = inject(ToastService);

  /** Member created in Step 1 — required to verify/resend. */
  @Input({ required: true }) memberId!: number;

  /** Delivery channels the OTP was sent through, e.g. ["SMS", "EMAIL"]. */
  @Input() channels: string[] = [];

  /** OTP validity window in minutes; drives the expiry countdown. */
  @Input() expiresInMinutes = 5;

  /** false => no channel enabled / OTP not sent: show guidance, disable Verify. */
  @Input() otpSent = true;

  /** Guiding message carried over from the Save response. */
  @Input() otpMessage = '';

  /** Emitted once the OTP is verified — the parent redirects. */
  @Output() verified = new EventEmitter<void>();

  /** Single 6-digit code control, wrapped in a tiny group for formControlName. */
  form = new FormGroup({
    code: new FormControl('', { nonNullable: true }),
  });

  /** mm:ss expiry countdown, refreshed every second. */
  countdown = '';

  /** True once the expiry window has elapsed. */
  expired = false;

  /** Seconds remaining on the resend cooldown (0 => resend enabled). */
  cooldown = 0;

  /** Verify button busy state. */
  verifying = false;

  private expiryInterval: any = null;
  private cooldownInterval: any = null;
  private endTime = 0;

  constructor(@Inject(PLATFORM_ID) private platformId: object) {}

  // (Re)start the expiry countdown once memberId / expiry first arrive. The
  // component can render before the parent sets memberId, so we react here.
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['memberId'] || changes['expiresInMinutes']) {
      if (this.memberId != null && isPlatformBrowser(this.platformId)) {
        this.startExpiryCountdown();
      }
    }
  }

  ngOnDestroy(): void {
    this.clearTimers();
  }

  /** Current code string value. */
  code(): string {
    return this.form.controls.code.value ?? '';
  }

  /** Verify is allowed only when an OTP was sent and the code is 6 digits. */
  canVerify(): boolean {
    return this.otpSent && /^[0-9]{6}$/.test(this.code());
  }

  /** Sub-header text describing where the OTP was delivered. */
  get subHeader(): string {
    const where = this.channels.length ? this.channels.join(', ') : 'your registered contact';
    return `OTP sent via ${where}. Enter the 6-digit code.`;
  }

  /** Verify the entered OTP. */
  verify(): void {
    if (!this.canVerify() || this.verifying) {
      return;
    }

    this.verifying = true;
    this.memberService.verifyOtp(this.memberId, this.code()).subscribe({
      next: (res: any) => {
        this.verifying = false;
        if (res?.success === true) {
          this.toast.success(res?.message || 'KYC Verified');
          this.verified.emit();
        } else {
          // message carries "Invalid OTP. N attempt(s) left.", "OTP expired...",
          // "Maximum attempts exceeded..." — always surface it.
          this.toast.error(res?.message || 'Invalid OTP');
          this.clearCode();
        }
      },
      error: () => {
        this.verifying = false;
        this.toast.error('Verification failed. Please try again.');
      },
    });
  }

  /** Resend the OTP (guarded by the 30s cooldown). */
  resend(): void {
    if (this.cooldown > 0) {
      return;
    }

    this.memberService.resendOtp(this.memberId).subscribe({
      next: (res: any) => {
        if (res?.success) {
          this.toast.success(res?.message || 'OTP resent');
          this.clearCode();
          if (isPlatformBrowser(this.platformId)) {
            this.startExpiryCountdown();
            this.startCooldown();
          }
        } else {
          this.toast.error(res?.message || 'Could not resend OTP');
        }
      },
      error: () => {
        this.toast.error('Could not resend OTP. Please try again.');
      },
    });
  }

  /** Reset the code input and refocus the first cell. */
  private clearCode(): void {
    this.form.controls.code.reset('');
    if (isPlatformBrowser(this.platformId)) {
      // Defer so the cleared control is rendered before we refocus.
      setTimeout(() => {
        const first = document.querySelector<HTMLInputElement>('app-otp-step .p-inputotp-input');
        first?.focus();
      });
    }
  }

  // ⏱ Countdown derived from an end timestamp so it survives re-renders.
  private startExpiryCountdown(): void {
    clearInterval(this.expiryInterval);
    this.expired = false;
    this.endTime = Date.now() + this.expiresInMinutes * 60 * 1000;

    const tick = () => {
      const diff = this.endTime - Date.now();
      if (diff <= 0) {
        clearInterval(this.expiryInterval);
        this.expiryInterval = null;
        this.countdown = '00:00';
        this.expired = true;
        return;
      }
      const totalSeconds = Math.floor(diff / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      this.countdown = `${minutes.toString().padStart(2, '0')}:${seconds
        .toString()
        .padStart(2, '0')}`;
    };

    tick();
    this.expiryInterval = setInterval(tick, 1000);
  }

  // 🔁 30-second resend cooldown.
  private startCooldown(): void {
    clearInterval(this.cooldownInterval);
    this.cooldown = 30;

    this.cooldownInterval = setInterval(() => {
      this.cooldown -= 1;
      if (this.cooldown <= 0) {
        this.cooldown = 0;
        clearInterval(this.cooldownInterval);
        this.cooldownInterval = null;
      }
    }, 1000);
  }

  private clearTimers(): void {
    clearInterval(this.expiryInterval);
    clearInterval(this.cooldownInterval);
    this.expiryInterval = null;
    this.cooldownInterval = null;
  }
}
